import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "@/lib/supabaseClient";

const AuthContext = createContext(null);

/**
 * AuthProvider — single source of truth for session + role data.
 *
 * Exposes:
 *   session       — Supabase session object or null
 *   user          — session.user shortcut
 *   profile       — row from lbs_user_profiles
 *   assignments   — rows from lbs_user_assignments (role + scope)
 *   roles         — string[] of distinct roles the user holds
 *   loading       — true while initial bootstrap is in flight
 *   signIn / signOut / resetPassword
 *   hasRole(roleOrRoles) — boolean helper
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      setAssignments([]);
      return;
    }
    const [{ data: p }, { data: a }] = await Promise.all([
      supabase.from("lbs_user_profiles").select("*").eq("id", userId).maybeSingle(),
      supabase
        .from("lbs_user_assignments")
        .select("id, role, is_primary, client_id, site_id, bay_id, approved_booker_id")
        .eq("user_id", userId),
    ]);
    setProfile(p || null);
    setAssignments(a || []);
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      await loadProfile(s?.user?.id);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, s) => {
      setSession(s);
      await loadProfile(s?.user?.id);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(async (email, password) => {
    return supabase.auth.signInWithPassword({ email, password });
  }, []);

  const signOut = useCallback(async () => {
    // supabase-js can throw an internal "Response with null body status cannot
    // have body" TypeError when the sign-out request returns 204 No Content.
    // The client-side session is already cleared by that point, so the error
    // is only console noise — swallow it to keep the log clean.
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("supabase signOut swallowed error:", e?.message || e);
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
  }, []);

  const roles = useMemo(
    () => Array.from(new Set(assignments.map((a) => a.role))),
    [assignments]
  );

  const hasRole = useCallback(
    (roleOrRoles) => {
      const list = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
      return roles.some((r) => list.includes(r));
    },
    [roles]
  );

  const value = useMemo(
    () => ({
      session,
      user: session?.user || null,
      profile,
      assignments,
      roles,
      loading,
      signIn,
      signOut,
      resetPassword,
      hasRole,
    }),
    [session, profile, assignments, roles, loading, signIn, signOut, resetPassword, hasRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
